// audio.js
// iOS/iPadOS suspends the WebAudio context whenever the device sleeps, the tab is
// backgrounded, or Safari reclaims memory. Howler only unlocks the context on the
// first user gesture and then drops its listeners, so once the context is
// suspended nothing ever resumes it and every later beep fails silently. These
// helpers keep the context alive for the whole life of the page instead of only
// until the first interaction.
import { Howler } from "howler";

// Any of these are treated as a chance to bring the audio context back.
const RESUME_EVENTS = ["click", "touchstart", "touchend", "keydown"];

let installed = false;

export const isAudioRunning = () =>
  Boolean(Howler.ctx) && Howler.ctx.state === "running";

// Ask the browser to resume a suspended context. Safe to call as often as we like.
export const resumeAudio = () => {
  try {
    const ctx = Howler.ctx;
    if (!ctx || ctx.state === "running") return;
    const resuming = ctx.resume();
    if (resuming && typeof resuming.catch === "function") {
      resuming.catch((err) => console.error("[audio] resume rejected", err));
    }
  } catch (err) {
    console.error("[audio] resume failed", err);
  }
};

// Attach the resume hooks for the lifetime of the page. Unlike the original
// one-shot unlock, these listeners are deliberately never removed - the iPad can
// suspend audio any number of times during a multi-day shift.
// onResume is invoked after every resume attempt so callers can restart their own
// playback (see BeepManager.wakeUp).
export const installAudioKeepAlive = (onResume) => {
  const handleGesture = () => {
    resumeAudio();
    if (onResume) onResume();
  };

  const handleVisibility = () => {
    if (document.visibilityState === "visible") handleGesture();
  };

  if (installed) return;
  installed = true;

  RESUME_EVENTS.forEach((evt) =>
    document.addEventListener(evt, handleGesture, { passive: true })
  );
  document.addEventListener("visibilitychange", handleVisibility);
  // pageshow fires when Safari restores the page from the back/forward cache,
  // which is a common way for a parked iPad to come back with dead audio.
  window.addEventListener("pageshow", handleGesture);
  window.addEventListener("focus", handleGesture);
};
